import { Input } from "../../../components/Input/input";
import { useLocation, useParams } from "react-router-dom";
import styled from "styled-components";
import * as yup from "yup";
import { useFormik } from "formik";
import { StyledButton } from "../../../components/Button/button";
import { theme } from "../../../helpers/theme";
import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInputProps,
  Select,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { ToastOptions, toast } from "react-toastify";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { useEffect, useState } from "react";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { pl } from "date-fns/locale";
import dayjs, { Dayjs } from "dayjs";
import { pricelistAdd } from "../../../services/pricelists/pricelist-add";
import { PriceList } from "../../../services/warehouses/pricelist-get-by-id/types";

export const PricelistAddForm = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const { pricelist } = state;

  // useEffect(() => {
  //   console.log(pricelist);
  // }, [pricelist]);

  const [fromDate, setFromDate] = useState<
    Dayjs | Date | string | null | undefined
  >();
  const [fromDateFixed, setFromDateFixed] = useState<boolean>(false);
  const [toDate, setToDate] = useState<
    Dayjs | Date | string | null | undefined
  >();
  const [dateError, setDateError] = useState<boolean>(false);
  const [dateFromInvalid, setDateFromInvalid] = useState<string | null>(null);
  const [dateToInvalid, setDateToInvalid] = useState<string | null>(null);

  useEffect(() => {
    setToDate(dayjs(fromDate).add(1, "day").toDate());
  }, []);

  useEffect(() => {
    if (fromDate === null || toDate === null) {
      setDateError(true);
    } else if (fromDate != null && toDate != null && fromDate > toDate) {
      setDateError(true);
    } else {
      setDateError(false);
    }
  }, [fromDate, toDate]);

  const settings: ToastOptions<{}> = {
    position: "top-center",
    autoClose: 3000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: false,
    theme: "light",
  };

  if (toast) {
    toast.dismiss("400-error");
  }

  const AddPricelistMutation = useMutation(pricelistAdd, {
    onSuccess: (res) => {
      toast.success("Dodano pomyślnie!", {
        toastId: "AddSuccessfully",
        ...settings,
      });
    },
    onError: (err: any) => {
      if (
        err.response.data.errors_debug[0] ===
        "Zła data, już istnieje rodzaj oferty i usługa magazynu pokrywająca się z datą."
      ) {
        toast.error(
          "Istnieje już usługa magazynu i rodzaj oferty pokrywająca się ze wskazanym zakresem dat",
          { toastId: "AddFailed", ...settings }
        );
      }
    },
  });

  const validationSchema = yup.object({
    jednostka_rozliczenia: yup
      .number()
      .required("Jednostka rozliczenia jest wymagana"),
    stawka_netto: yup
      .string()
      .matches(/^[0-9]+(\.[0-9]{1,2})?$/, "Błędny format!")
      .min(0, "Stawka netto nie może być ujemna")
      .max(9, "Maksymalna długość to 9 znaków")
      .typeError("Błędny format!")
      .required("Stawka netto jest wymagana"),
    nazwa: yup
      .string()
      .max(100, "Maksymalna długość to 100 znaków")
      .required("Nazwa jest wymagana"),
    nazwa_na_fakture: yup
      .string()
      .max(100, "Maksymalna długość to 100 znaków")
      .required("Nazwa na fakturze jest wymagana"),
    usluga_magazynu: yup.number().required("Usługa magazynu jest wymagana"),
    rodzaj_oferty: yup.number().required("Rodzaj oferty jest wymagany"),
  });
  const formik = useFormik({
    initialValues: {
      jednostka_rozliczenia: 0,
      stawka_netto: "",
      // wazny_od: pData.wazny_od,
      // wazny_do: pData.wazny_do,
      nazwa: "",
      nazwa_na_fakture: "",
      usluga_magazynu: 0,
      rodzaj_oferty: 0,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const body = {
        magazyn_id: id,
        jednostka_rozliczenia: values.jednostka_rozliczenia,
        stawka_netto: parseFloat(values.stawka_netto.replace(",", ".")),
        wazny_od: fromDate,
        wazny_do: toDate,
        nazwa: values.nazwa,
        nazwa_na_fakture: values.nazwa_na_fakture,
        usluga_magazynu: values.usluga_magazynu,
        rodzaj_oferty: values.rodzaj_oferty,
      };

      // console.log(body);
      AddPricelistMutation.mutate(body);
    },
  });

  useEffect(() => {
    const filteredPrices = pricelist.filter(
      (p: PriceList) =>
        p.rodzaj_oferty === formik.values.rodzaj_oferty &&
        p.usluga_magazynu === formik.values.usluga_magazynu
    );

    if (filteredPrices.length !== 0) {
      const newestDate = filteredPrices.reduce((acc: any, p: any) => {
        const date = new Date(p.wazny_do);
        return date > acc ? date : acc;
      }, new Date(0));
      const oneDay = 24 * 60 * 60 * 1000; // 1 day in milliseconds
      const newFromDate = new Date(newestDate.getTime() + oneDay);
      setFromDate(newFromDate);
      setFromDateFixed(true);
    } else {
      setFromDate(new Date());
      setFromDateFixed(false);
    }
  }, [pricelist, formik.values]);
  // if (EditClient.isLoading) {
  //   return <LoadingBackDrop loading={true} />;
  // }
  return (
    <BasicFormWrapper onSubmit={formik.handleSubmit}>
      <FormWrapper>
        <FormControl fullWidth>
          <InputLabel id="jednostka_rozliczenia-label">
            Jednostka rozliczenia
          </InputLabel>
          <Select
            placeholder="Jednostka rozliczenia"
            labelId="jednostka_rozliczenia-label"
            label="Jednostka rozliczenia"
            id="jednostka_rozliczenia"
            name="jednostka_rozliczenia"
            value={formik.values.jednostka_rozliczenia}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            style={{ backgroundColor: "white" }}
          >
            <MenuItem value={0}>Paletodoba</MenuItem>
            <MenuItem value={1}>Paleta</MenuItem>
            <MenuItem value={2}>Paleta mix</MenuItem>
            <MenuItem value={3}>Etykieta lub karton</MenuItem>
            <MenuItem value={4}>Sztuka lub karton</MenuItem>
            <MenuItem value={5}>m²</MenuItem>
            <MenuItem value={6}>m³</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="usluga_magazynu-label">Usługa magazynu</InputLabel>
          <Select
            placeholder="Usługa magazynu"
            labelId="usluga_magazynu-label"
            label="Usługa magazynu"
            id="usluga_magazynu"
            name="usluga_magazynu"
            value={formik.values.usluga_magazynu}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            style={{ backgroundColor: "white" }}
          >
            <MenuItem value={0}>Składowanie</MenuItem>
            <MenuItem value={1}>Crossdocking</MenuItem>
            <MenuItem value={2}>Fullfilment</MenuItem>
            <MenuItem value={3}>Etykietowanie</MenuItem>
            <MenuItem value={4}>Pakowanie</MenuItem>
            <MenuItem value={5}>Sortowanie</MenuItem>
            <MenuItem value={6}>E_commerce</MenuItem>
            <MenuItem value={7}>Wysyłka</MenuItem>
            <MenuItem value={8}>Inne</MenuItem>
            <MenuItem value={9}>Przyjecie_towaru</MenuItem>
            <MenuItem value={10}>Wydanie_towaru</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="rodzaj_oferty-label">Rodzaj oferty</InputLabel>
          <Select
            placeholder="Rodzaj oferty"
            labelId="rodzaj_oferty-label"
            label="Rodzaj oferty"
            id="rodzaj_oferty"
            name="rodzaj_oferty"
            value={formik.values.rodzaj_oferty}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            style={{ backgroundColor: "white" }}
          >
            <MenuItem value={0}>Magazyn dedykowany</MenuItem>
            <MenuItem value={1}>Multimagazyn</MenuItem>
          </Select>
        </FormControl>
        <Input
          label="Stawka netto"
          id="stawka_netto"
          name="stawka_netto"
          value={formik.values.stawka_netto}
          onChange={formik.handleChange}
          error={
            formik.touched.stawka_netto && Boolean(formik.errors.stawka_netto)
          }
          helperText={formik.touched.stawka_netto && formik.errors.stawka_netto}
          onBlur={formik.handleBlur}
          variant="filled"
          style={{ marginTop: 11 }}
        />
        {/* <Input
            label="Ważny od"
            id="wazny_od"
            name="wazny_od"
            value={formik.values.wazny_od}
            onChange={formik.handleChange}
            error={formik.touched.wazny_od && Boolean(formik.errors.wazny_od)}
            helperText={formik.touched.wazny_od && formik.errors.wazny_od}
            onBlur={formik.handleBlur}
            variant="filled"
            style={{ marginTop: 11 }}
          />
          <Input
            label="Ważny do"
            id="wazny_do"
            name="wazny_do"
            value={formik.values.wazny_do}
            onChange={formik.handleChange}
            error={formik.touched.wazny_do && Boolean(formik.errors.wazny_do)}
            helperText={formik.touched.wazny_do && formik.errors.wazny_do}
            onBlur={formik.handleBlur}
            variant="filled"
            style={{ marginTop: 11 }}
          /> */}
        <LocalizationProvider adapterLocale={pl} dateAdapter={AdapterDateFns}>
          <DatePickersWrapper>
            <DesktopDatePicker
              views={["day"]}
              label="Ważny od"
              // disablePast
              disabled={fromDateFixed}
              value={fromDate}
              InputProps={
                { disableUnderline: true } as Partial<OutlinedInputProps>
              }
              onError={(error) => setDateFromInvalid(error)}
              onChange={(newValue) => {
                setFromDate(newValue);
              }}
              renderInput={(params) => (
                <InputWrapper style={{ justifyContent: "flex-start" }}>
                  <Input
                    {...params}
                    helperText={null}
                    variant="filled"
                    sx={{
                      width: "100%",
                      // minWidth: "300px",
                    }}
                  />
                </InputWrapper>
              )}
            />

            <DesktopDatePicker
              views={["day"]}
              label="Ważny do"
              disablePast
              minDate={fromDate}
              value={toDate}
              InputProps={
                { disableUnderline: true } as Partial<OutlinedInputProps>
              }
              onError={(error) => setDateToInvalid(error)}
              onChange={(newValue) => {
                setToDate(newValue);
              }}
              renderInput={(params) => (
                <InputWrapper style={{ justifyContent: "flex-end" }}>
                  <Input
                    {...params}
                    helperText={null}
                    variant="filled"
                    sx={{
                      //  marginRight: "13px",
                      width: "100%",
                      // minWidth: "300px",
                    }}
                  />
                </InputWrapper>
              )}
            />
          </DatePickersWrapper>
        </LocalizationProvider>
        <Input
          label="Nazwa"
          id="nazwa"
          name="nazwa"
          value={formik.values.nazwa}
          onChange={formik.handleChange}
          error={formik.touched.nazwa && Boolean(formik.errors.nazwa)}
          helperText={formik.touched.nazwa && formik.errors.nazwa}
          onBlur={formik.handleBlur}
          variant="filled"
          style={{ marginTop: 11 }}
        />
        <Input
          label="Nazwa na fakturze"
          id="nazwa_na_fakture"
          name="nazwa_na_fakture"
          value={formik.values.nazwa_na_fakture}
          onChange={formik.handleChange}
          error={
            formik.touched.nazwa_na_fakture &&
            Boolean(formik.errors.nazwa_na_fakture)
          }
          helperText={
            formik.touched.nazwa_na_fakture && formik.errors.nazwa_na_fakture
          }
          onBlur={formik.handleBlur}
          variant="filled"
          style={{ marginTop: 11 }}
        />
        <StyledButton
          disabled={
            !formik.isValid ||
            AddPricelistMutation.isLoading ||
            dateError ||
            (dateFromInvalid === null ? false : true) ||
            (dateToInvalid === null ? false : true)
          }
          type="submit"
          variant="contained"
        >
          Zapisz
        </StyledButton>
        {/* <StyledButton onClick={() => setToDate(null)} variant="contained">Wyczyść kalendarz</StyledButton> */}
      </FormWrapper>
    </BasicFormWrapper>
  );
};

export default PricelistAddForm;

const BasicFormWrapper = styled.form`
  width: 100%;
  margin-bottom: 40px;
  display: flex;
  justify-content: center;
  // padding: 0 40px;

  @media (${theme.device.laptop}) {
    width: 100%;
    // padding: 0 10px;
  }
`;

const FormWrapper = styled.div`
  width: 90%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 260px;
  max-width: 420px;
  gap: 25px;

  @media (${theme.device.laptop}) {
    width: 100%;
  }
`;

const DatePickersWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media ${theme.device.Mlaptop} {
    flex-direction: column;
    gap: 10px;
  }
  width: 100%;
`;

const InputWrapper = styled.div`
  width: 49%;
  display: flex;

  @media ${theme.device.Mlaptop} {
    width: 100%;
  }
`;
